import { useCallback, useEffect, useRef, useState } from 'react';
import { useMemo } from 'react';

import { FormHandles, SubmitHandler } from '@unform/core';
import { Form } from '@unform/web';
import { format } from 'date-fns';
import { FiSearch } from 'react-icons/fi';
import { IoIosArrowBack } from 'react-icons/io';
import { MdPersonSearch } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  Button,
  Input,
  InputDate,
  InputMask,
  Layout,
  Modal,
  Textarea,
} from '~/components';
import { useAuth } from '~/hooks/auth';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import {
  PageHeader,
  PageHeaderBackButton,
  PageHeaderBackButtonLabel,
  PageHeaderSaveButton,
  Container,
  FormContainer,
  FieldSet,
  FormLabel,
  FieldsWrapper,
  RadioInput,
  PageHeaderButtonsContainer,
  FindedStudentMessageContainer,
  SearchStudentContainer,
} from './styles';

type FormProps = {
  image_url: string;
  name: string;
  email: string;
  cellphone: string;
  address_zip_code: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_city: string;
  address_state: string;
  address_country: string;
  address_neighborhood: string;
  professional_id: number;
  cpf: string;
  genre: string;
  birth_date: string;
  date_registration: string;
  observations: string;
};

type ModalFormProps = {
  cpf: string;
};

export type LinkedStudent = {
  birth_date: string;
  cpf: string;
  created_at: string;
  genre: string;
  gym_id: string | null;
  id: number;
  observations: string | null;
  updated_at: string;
  user_id: number;
};

const StudentRegister = () => {
  const { goBack } = useHistory();
  const { user } = useAuth();
  const formRef = useRef<FormHandles>(null);
  const modalFormRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [gender, setGender] = useState('male');
  const [searchUserModalVisible, setSearchUserModalVisible] =
    useState<boolean>(false);
  const [searchedStudent, setSearchedStudent] = useState<LinkedStudent>(
    {} as LinkedStudent,
  );

  useEffect(() => {
    const todayDate = format(new Date(), 'yyyy-MM-dd');

    formRef.current?.setFieldValue('date_registration', todayDate);
  }, []);

  const genderVariations = useMemo(
    () => [
      { id: 'male', label: 'Masculino' },
      { id: 'female', label: 'Feminino' },
      { id: 'other', label: 'Outros' },
    ],
    [],
  );

  const GenderOptions = useMemo(
    () =>
      genderVariations.map((item) => (
        <RadioInput
          key={item.id}
          isSelected={item.id === gender}
          onClick={() => setGender(item.id)}
        >
          <div className="radio">
            <div className="filled" />
          </div>

          <p>{item.label}</p>
        </RadioInput>
      )),
    [gender, genderVariations],
  );

  const inputStyle = {
    height: 35,
    padding: '0 1.7rem',
  };

  const textArea = {
    width: '100%',
    marginTop: 5,

    height: '19rem',
    padding: '2rem',
    borderRadius: '1rem',
  };

  const handleSaveObservation = (observation: string) => {
    const newObservation = `/${user.id}${observation.replace(
      `${user.id}&&!!`,
      '',
    )}${user.id}&&!!/${user.id}`;

    return `${newObservation}`;
  };

  const handleOpenSearchUserModal = () =>
    setSearchUserModalVisible((oldValue) => !oldValue);

  const handleSubmit: SubmitHandler<FormProps> = useCallback(
    async (data) => {
      setLoading(true);

      const formData = {
        ...data,
        professional_id: user.professional.id,
        genre: gender,
        cpf: data.cpf.replace(/\D/gim, ''),
        observations: handleSaveObservation(data.observations),
      };

      const schema = Yup.object().shape({
        name: Yup.string().required('O campo é obrigatório!'),
        birth_date: Yup.string().required('O campo é obrigatório!'),
        cpf: Yup.string().required('O campo é obrigatório!'),
        observations: Yup.string(),
        email: Yup.string()
          .required('O campo é obrigatório!')
          .email('Email inválido!'),
      });

      try {
        await schema.validate(data, { abortEarly: false });

        await api.post('students', formData);

        setLoading(false);

        toast.success('Aluno cadastrado com sucesso!');

        goBack();
      } catch (err) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
        }
        console.log('err:', err);

        toast.error('Ocorreu um erro ao cadastrar o aluno!');
      }
    },
    [gender, goBack, user],
  );

  const handleModalSubmit: SubmitHandler<ModalFormProps> = useCallback(
    async (data) => {
      setLoading(true);

      const formData = {
        cpf: data.cpf.replace(/\D/gim, ''),
      };

      const schema = Yup.object().shape({
        cpf: Yup.string().required('O campo é obrigatório!'),
      });

      try {
        await schema.validate(data, { abortEarly: false });

        const response = await api.get('students/cpf', { params: formData });

        if (Object.keys(response.data).length > 0) {
          setSearchedStudent(response.data);
        } else {
          toast.error('Aluno não encontrado');
        }

        setLoading(false);
      } catch (err) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }

        console.log('err:', err);

        toast.error('Ocorreu um erro ao pelo aluno!');
      }
    },
    [],
  );

  const handleLinkStudent = async () => {
    try {
      setLoading(true);

      await api.post('students/attach', { id: searchedStudent.id });

      setSearchUserModalVisible(false);

      setLoading(false);

      goBack();
    } catch (error) {
      setLoading(false);
      console.log('error:', error);
    }
  };

  const handleCancelStudentLink = () => {
    setSearchedStudent({} as LinkedStudent);
    setSearchUserModalVisible(false);
  };

  return (
    <Layout title="MEUS ALUNOS">
      <PageHeader>
        <PageHeaderBackButton onClick={() => goBack()}>
          <IoIosArrowBack />
          <PageHeaderBackButtonLabel>novo aluno</PageHeaderBackButtonLabel>
        </PageHeaderBackButton>

        <PageHeaderButtonsContainer>
          <MdPersonSearch
            size={30}
            onClick={() => handleOpenSearchUserModal()}
          />
          <PageHeaderSaveButton
            disabled={loading}
            onClick={() => formRef.current?.submitForm()}
          >
            Salvar
          </PageHeaderSaveButton>
        </PageHeaderButtonsContainer>
      </PageHeader>

      <Container>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <FormContainer>
            <FieldSet>
              <FormLabel>Data de cadastro:</FormLabel>
              <InputDate
                name="date_registration"
                containerStyle={inputStyle}
                fontSize="1.5rem"
                disabled
              />
            </FieldSet>

            <FieldsWrapper>
              <FieldSet>
                <FormLabel>Nome: *</FormLabel>
                <Input
                  name="name"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Gênero: *</FormLabel>
                <>{GenderOptions}</>
              </FieldSet>

              <FieldSet>
                <FormLabel>Email: *</FormLabel>
                <Input
                  name="email"
                  type="email"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Data de nascimento: *</FormLabel>
                <InputDate
                  name="birth_date"
                  type="date"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>CPF: *</FormLabel>
                <InputMask
                  name="cpf"
                  mask="999.999.999-99"
                  containerStyle={inputStyle}
                  fontSize="1.5rem"
                />
              </FieldSet>

              <FieldSet>
                <FormLabel>Observações:</FormLabel>
                <Textarea
                  name="observations"
                  fontSize="1.5rem"
                  containerStyle={textArea}
                />
              </FieldSet>
            </FieldsWrapper>
          </FormContainer>
        </Form>

        <Modal
          isOpen={searchUserModalVisible}
          setOpen={setSearchUserModalVisible}
        >
          {Object.keys(searchedStudent).length > 0 ? (
            <FindedStudentMessageContainer>
              <h3>Aluno encontrado!</h3>

              <div>
                <Button onClick={() => handleLinkStudent()}>Vincular</Button>
                <Button onClick={() => handleCancelStudentLink()}>
                  Cancelar
                </Button>
              </div>
            </FindedStudentMessageContainer>
          ) : (
            <Form ref={modalFormRef} onSubmit={handleModalSubmit}>
              <SearchStudentContainer>
                <h4>Procurar aluno pelo CPF</h4>

                <div>
                  <InputMask
                    name="cpf"
                    mask="999.999.999-99"
                    placeholder="Ex: 000.000.000-00"
                  />

                  <FiSearch
                    size={30}
                    color="#000"
                    onClick={() => modalFormRef.current?.submitForm()}
                  />
                </div>
              </SearchStudentContainer>
            </Form>
          )}
        </Modal>
      </Container>
    </Layout>
  );
};

export default StudentRegister;
